import { useMemo, Fragment, useCallback } from 'react';
import clsx from 'clsx';
import { Close, TicketOutline } from 'react-ionicons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getUserHistory,
  pinVisitedTicket,
  removeHistory,
  removeHistoryItem,
  unpinTicket,
} from 'api/users/api';
import {
  UserHistoryActionResponse,
  TicketHistory,
  UserHistoryResponse,
} from 'api/users/schemas/UserHistory';
import NavbarTooltip from './NavbarTooltip';
import Skeleton from 'components/Skeleton/Skeleton';
import useAutoRefetchUserHistory from './useAutoRefetchUserHistory';
import Loader from 'components/Loader';
import { useHotkey } from 'hooks/useHotkey';

interface ViewsMenuProps {
  isWide: boolean;
  onFirstDataFetch?: () => void;
}

const ViewsMenu = ({ isWide }: ViewsMenuProps) => {
  useAutoRefetchUserHistory();
  const { data, isLoading } = useQuery<UserHistoryResponse, Error>(
    ['userHistory'],
    getUserHistory,
  );

  const list = useMemo(() => {
    return [...(data?.pinned || []), ...(data?.visited || [])];
  }, [data?.pinned, data?.visited]);

  return (
    <div className="flex flex-col gap-2">
      {isLoading ? (
        Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className={clsx(
              'flex h-10 flex-row items-center gap-2.5 p-2',
              !isWide && 'pl-3',
            )}
          >
            <Skeleton className="h-6 w-6 flex-none !rounded-full" />
            <Skeleton className="h-5 w-full" />
          </div>
        ))
      ) : list.length ? (
        list.map((item) => (
          <Fragment key={item.id}>
            <NavbarTooltip title={item.title} hide={isWide}>
              <ViewMenuItem
                data={item}
                // onPinClick={handlePinClick}
                isWide={isWide}
              />
            </NavbarTooltip>
          </Fragment>
        ))
      ) : (
        <div
          className={clsx(
            'whitespace-nowrap text-center text-xs font-semibold transition-opacity duration-300 ease-out',
            isWide ? '' : 'opacity-0',
          )}
        >
          No recent views
        </div>
      )}
    </div>
  );
};

export default ViewsMenu;

interface ViewMenuItemProps {
  data: TicketHistory;
  // onPinClick: (event: React.MouseEvent, id: string, pinned: boolean) => void;
  isWide: boolean;
}

const ViewMenuItem = ({ data /*, onPinClick*/, isWide }: ViewMenuItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isSelected = useMemo(
    () =>
      data.history_type.link_template.replace(
        '{{ ticket_id }}',
        data.resource_value,
      ) === location.pathname,
    [data.history_type.link_template, data.resource_value, location.pathname],
  );

  const handleClick = () => {
    navigate(
      data.history_type.link_template.replace(
        '{{ ticket_id }}',
        data.resource_value,
      ),
    );
  };

  const queryClient = useQueryClient();
  const { mutate: pinTicketMutation, isLoading: isPinLoading } = useMutation<
    UserHistoryActionResponse,
    Error,
    string
  >((historyId) => pinVisitedTicket(historyId), {
    onSuccess: () => queryClient.invalidateQueries(['userHistory']),
  });

  const { mutate: unpinTicketMutation, isLoading: isUnpinLoading } =
    useMutation<UserHistoryActionResponse, Error, string>(
      (historyId) => unpinTicket(historyId),
      {
        onSuccess: () => queryClient.invalidateQueries(['userHistory']),
      },
    );

  const { mutate: removeTicketMutation, isLoading: isRemoveLoading } =
    useMutation<UserHistoryActionResponse, Error, string>(
      (historyId) => removeHistoryItem(historyId),
      {
        onSuccess: () => queryClient.invalidateQueries(['userHistory']),
      },
    );

  const onPinClick = useCallback(
    (event: React.MouseEvent, id: string, pinned: boolean) => {
      event.preventDefault();
      event.stopPropagation();
      if (pinned) {
        unpinTicketMutation(id);
      } else {
        pinTicketMutation(id);
      }
    },
    [unpinTicketMutation, pinTicketMutation],
  );

  const onRemove = useCallback(
    (event: React.MouseEvent, id: string) => {
      event.preventDefault();
      event.stopPropagation();
      removeTicketMutation(id);
    },
    [removeTicketMutation],
  );

  const pinUnpinHistoryItem = useCallback(
    (event: KeyboardEvent) => {
      if (!isSelected) return;
      event.preventDefault();
      if (data.pinned) {
        unpinTicketMutation(data.id);
      } else {
        pinTicketMutation(data.id);
      }
    },
    [isSelected, data.pinned, data.id, unpinTicketMutation, pinTicketMutation],
  );

  useHotkey('p', pinUnpinHistoryItem, { ctrl: true, meta: true });

  return (
    <div
      key={data.id}
      className={clsx(
        'group box-border flex cursor-pointer flex-row items-center gap-2 rounded border-2 border-transparent p-[6px] transition-all duration-300 ease-out hover:bg-navigation-surface-primary-hover',
        isSelected &&
          '!border-navigation-accent-purple !bg-transparent text-content-base-default',
      )}
      onClick={handleClick}
    >
      <div
        className={clsx(
          'flex-none transition-all duration-150 ease-out',
          isSelected && 'text-content-base-default',
          !isWide && 'pl-1',
        )}
      >
        <TicketOutline width="24px" height="24px" color="currentColor" />
      </div>
      <div
        className={clsx(
          'relative flex flex-1 flex-row items-center justify-between gap-2 whitespace-nowrap text-xs font-semibold',
          'w-[152px] transition-all duration-300 ease-out',
          isWide ? '' : ' opacity-0',
        )}
      >
        <div className="relative z-10 flex max-w-[108px] flex-1 overflow-hidden">
          <div className="truncate">{data.title}</div>
        </div>
        <div
          className={clsx(
            'absolute left-[70px] flex w-20 translate-x-6 flex-row items-center justify-end gap-2 transition-transform duration-150 ease-out',
            'group-hover:translate-x-0',
            isSelected && '!translate-x-0',
          )}
        >
          <div
            className="relative h-4 w-4 flex-none"
            onClick={(event) => onPinClick(event, data.id, data.pinned)}
          >
            <div className="-m-1.5 h-7 w-7 p-1.5">
              {isPinLoading || isUnpinLoading ? (
                <Loader animationClassname="!h-4 !w-4 !border-2" />
              ) : (
                <span
                  className={clsx(
                    'absolute opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100',
                    data.pinned &&
                      'opacity-100 group-hover:text-navigation-accent-purple',
                    data.pinned &&
                      isSelected &&
                      'text-navigation-accent-purple',
                  )}
                >
                  <PinIcon width="16px" height="16px" color="currentColor" />
                </span>
              )}
            </div>
          </div>
          <span
            className={clsx(isSelected && 'hidden')}
            onClick={(event) => onRemove(event, data.id)}
          >
            <div className="-m-1 h-6 w-6 p-1">
              {isRemoveLoading ? (
                <Loader animationClassname="!h-4 !w-4 !border-2" />
              ) : (
                <Close width="16px" height="16px" color="currentColor" />
              )}
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

export const ClearHistoryButton = ({ isWide }: { isWide: boolean }) => {
  const queryClient = useQueryClient();

  const { mutate: clearHistoryMutation, isLoading } = useMutation(
    () => removeHistory(),
    {
      onSuccess: () => queryClient.invalidateQueries(['userHistory']),
    },
  );

  return (
    <div
      className={clsx(
        'flex cursor-pointer flex-row gap-2 whitespace-nowrap text-xs font-semibold transition-opacity duration-300 ease-out hover:text-content-base-default',
        isWide ? '' : ' opacity-0',
      )}
      onClick={() => clearHistoryMutation()}
    >
      {isLoading && <Loader animationClassname="!h-4 !w-4 !border-2" />}
      Clear all
    </div>
  );
};

const PinIcon = ({
  width,
  height,
  color,
}: {
  width: string;
  height: string;
  color: string;
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 17"
      fill={color}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.66667 5.62929C4.6668 4.9986 4.84585 4.38089 5.18302 3.8479C5.5202 3.31491 6.00167 2.88852 6.57151 2.61824C7.14134 2.34796 7.77617 2.24488 8.40224 2.32099C9.02832 2.3971 9.61996 2.64926 10.1084 3.04819C10.5969 3.44712 10.9622 3.97645 11.1619 4.5747C11.3615 5.17295 11.3874 5.81556 11.2364 6.42791C11.0854 7.04026 10.7638 7.59722 10.309 8.03409C9.85409 8.47096 9.28461 8.76981 8.66667 8.89595V12.296C8.66667 12.4728 8.59643 12.6423 8.4714 12.7674C8.34638 12.8924 8.17681 12.9626 8 12.9626C7.82319 12.9626 7.65362 12.8924 7.5286 12.7674C7.40357 12.6423 7.33333 12.4728 7.33333 12.296V8.89595C6.58042 8.74226 5.90374 8.33316 5.4178 7.73789C4.93186 7.14261 4.66651 6.39772 4.66667 5.62929ZM6.326 11.696C6.33926 11.7825 6.33533 11.8709 6.31442 11.956C6.29352 12.041 6.25605 12.1211 6.20417 12.1917C6.15229 12.2623 6.087 12.322 6.01205 12.3673C5.93709 12.4127 5.85394 12.4428 5.76733 12.456C4.91533 12.586 4.23867 12.782 3.794 12.9993C3.57067 13.1086 3.438 13.208 3.36933 13.2806L3.35533 13.296C3.37733 13.322 3.41533 13.36 3.482 13.4086C3.66867 13.5466 3.98133 13.6986 4.42733 13.838C5.312 14.1146 6.57533 14.296 8 14.296C9.42467 14.296 10.688 14.1146 11.5727 13.838C12.0193 13.6986 12.3313 13.5466 12.518 13.4086C12.5847 13.36 12.6227 13.322 12.6447 13.296L12.6307 13.2806C12.562 13.208 12.4293 13.1086 12.206 12.9993C11.7613 12.782 11.0847 12.586 10.2327 12.456C10.1451 12.4439 10.0608 12.4146 9.98469 12.3698C9.90856 12.3249 9.84212 12.2653 9.78922 12.1945C9.73632 12.1237 9.69803 12.0431 9.67657 11.9574C9.65511 11.8716 9.65091 11.7825 9.66421 11.6951C9.67751 11.6078 9.70805 11.5239 9.75405 11.4484C9.80006 11.373 9.8606 11.3074 9.93217 11.2556C10.0037 11.2037 10.0849 11.1666 10.1709 11.1464C10.257 11.1262 10.3462 11.1234 10.4333 11.138C11.3587 11.278 12.1813 11.5026 12.7927 11.802C13.0973 11.9513 13.382 12.1353 13.598 12.3633C13.818 12.5946 14 12.91 14 13.296C14 13.8366 13.6507 14.2293 13.3107 14.4806C12.9573 14.742 12.4887 14.9486 11.9707 15.1106C10.924 15.4373 9.52133 15.6293 8 15.6293C6.47867 15.6293 5.07533 15.4373 4.03 15.1106C3.51133 14.9486 3.04333 14.742 2.69 14.4806C2.34933 14.2293 2 13.8366 2 13.296C2 12.9093 2.182 12.5946 2.402 12.3626C2.61867 12.1346 2.90267 11.9513 3.20733 11.8026C3.81867 11.5026 4.642 11.2786 5.566 11.138C5.65259 11.1247 5.74094 11.1286 5.82601 11.1495C5.91108 11.1704 5.9912 11.2079 6.06178 11.2598C6.13236 11.3117 6.19203 11.377 6.23736 11.4519C6.2827 11.5269 6.31282 11.6094 6.326 11.696Z"
      />
    </svg>
  );
};
